/**
 * Agent config form (§4.4.2).
 *
 * Plain `useState` — the form is five fields so react-hook-form is
 * overkill. Native <select> and <input type="checkbox"> spare us from
 * pulling in @radix-ui/react-select and @radix-ui/react-switch; the
 * custom ring + focus styles come from the scaffold's Tailwind CSS.
 *
 * Parent passes `initial` (current agent config) and receives the
 * minimal `UpdateAgentPayload` delta in `onReview`, plus a pre-built
 * YAML before/after pair so the diff modal can render without doing
 * another round-trip.
 */
import { useEffect, useMemo, useState } from 'react';
import type {
  AgentConfig,
  EffortLevel,
  PermissionMode,
  UpdateAgentPayload,
} from '@/api/agents';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface ConfigFormValues {
  description: string;
  enabled: boolean;
  permission_mode: PermissionMode;
  effort: EffortLevel;
  disable_adaptive_thinking: boolean;
  auto_restart: boolean;
  restart_sec: number;
}

export interface ConfigFormProps {
  initial: AgentConfig;
  onReview: (
    patch: UpdateAgentPayload,
    yaml: { before: string; after: string },
    version: string
  ) => void;
  submitLabel?: string;
}

function valuesFromAgent(agent: AgentConfig): ConfigFormValues {
  return {
    description: agent.description,
    enabled: agent.enabled,
    permission_mode: agent.permission_mode,
    effort: agent.effort ?? 'medium',
    disable_adaptive_thinking: agent.disable_adaptive_thinking ?? false,
    auto_restart: agent.systemd.auto_restart,
    restart_sec: agent.systemd.restart_sec,
  };
}

function buildPatch(
  initial: ConfigFormValues,
  current: ConfigFormValues
): UpdateAgentPayload {
  const patch: UpdateAgentPayload = {};
  if (initial.description !== current.description) patch.description = current.description;
  if (initial.enabled !== current.enabled) patch.enabled = current.enabled;
  if (initial.permission_mode !== current.permission_mode)
    patch.permission_mode = current.permission_mode;
  if (initial.effort !== current.effort) patch.effort = current.effort;
  if (initial.disable_adaptive_thinking !== current.disable_adaptive_thinking)
    patch.disable_adaptive_thinking = current.disable_adaptive_thinking;
  if (initial.auto_restart !== current.auto_restart) {
    patch.systemd = { ...(patch.systemd ?? {}), auto_restart: current.auto_restart };
  }
  if (initial.restart_sec !== current.restart_sec) {
    patch.systemd = { ...(patch.systemd ?? {}), restart_sec: current.restart_sec };
  }
  return patch;
}

function buildYaml(agent: AgentConfig, values: ConfigFormValues): string {
  return [
    `name: ${agent.name}`,
    `description: ${JSON.stringify(values.description)}`,
    `enabled: ${values.enabled}`,
    `permission_mode: ${values.permission_mode}`,
    `effort: ${values.effort}`,
    `disable_adaptive_thinking: ${values.disable_adaptive_thinking}`,
    `systemd:`,
    `  auto_restart: ${values.auto_restart}`,
    `  restart_sec: ${values.restart_sec}`,
  ].join('\n');
}

const nativeSelectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1',
  'text-sm transition-colors focus-visible:outline-none focus-visible:ring-2',
  'focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

const nativeTextareaClass = cn(
  'flex w-full rounded-md border border-input bg-background px-3 py-2',
  'text-sm transition-colors placeholder:text-muted-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
);

export function ConfigForm({ initial, onReview, submitLabel = 'Review changes' }: ConfigFormProps) {
  const baseValues = useMemo(() => valuesFromAgent(initial), [initial]);
  const [values, setValues] = useState<ConfigFormValues>(baseValues);

  // Re-sync on external updates (new agent fetched).
  useEffect(() => {
    setValues(baseValues);
  }, [baseValues]);

  const isDirty = useMemo(() => {
    return (
      values.description !== baseValues.description ||
      values.enabled !== baseValues.enabled ||
      values.permission_mode !== baseValues.permission_mode ||
      values.effort !== baseValues.effort ||
      values.disable_adaptive_thinking !== baseValues.disable_adaptive_thinking ||
      values.auto_restart !== baseValues.auto_restart ||
      values.restart_sec !== baseValues.restart_sec
    );
  }, [values, baseValues]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isDirty) return;
    const patch = buildPatch(baseValues, values);
    const yamlBefore = buildYaml(initial, baseValues);
    const yamlAfter = buildYaml(initial, values);
    onReview(patch, { before: yamlBefore, after: yamlAfter }, initial.version ?? '');
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          className={nativeTextareaClass}
          rows={3}
          value={values.description}
          onChange={(e) => setValues((v) => ({ ...v, description: e.target.value }))}
          placeholder="What does this agent do?"
        />
      </div>

      <div className="flex items-center justify-between rounded-md border border-border p-4">
        <div>
          <Label htmlFor="enabled" className="text-base">
            Enabled
          </Label>
          <p className="text-sm text-muted-foreground">
            Disabled agents are unloaded but their config is preserved.
          </p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2">
          <span className="sr-only">Enabled</span>
          <input
            id="enabled"
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={values.enabled}
            onChange={(e) => setValues((v) => ({ ...v, enabled: e.target.checked }))}
          />
        </label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="permission_mode">Permission mode</Label>
        <select
          id="permission_mode"
          className={nativeSelectClass}
          value={values.permission_mode}
          onChange={(e) =>
            setValues((v) => ({ ...v, permission_mode: e.target.value as PermissionMode }))
          }
        >
          <option value="default">Default (ask for dangerous actions)</option>
          <option value="plan">Plan mode (read-only)</option>
          <option value="bypassPermissions">Bypass permissions (advanced)</option>
        </select>
      </div>

      <div className="flex items-center justify-between rounded-md border border-border p-4">
        <div>
          <Label htmlFor="auto_restart" className="text-base">
            Auto-restart on failure
          </Label>
          <p className="text-sm text-muted-foreground">
            systemd restarts the agent if it exits unexpectedly.
          </p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2">
          <span className="sr-only">Auto-restart</span>
          <input
            id="auto_restart"
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={values.auto_restart}
            onChange={(e) => setValues((v) => ({ ...v, auto_restart: e.target.checked }))}
          />
        </label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="restart_sec">Restart delay (seconds)</Label>
        <Input
          id="restart_sec"
          type="number"
          min={1}
          max={600}
          value={values.restart_sec}
          onChange={(e) =>
            setValues((v) => ({ ...v, restart_sec: Number(e.target.value) || 0 }))
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="effort">Effort level</Label>
        <select
          id="effort"
          className={nativeSelectClass}
          value={values.effort}
          onChange={(e) =>
            setValues((v) => ({ ...v, effort: e.target.value as EffortLevel }))
          }
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="max">Max</option>
        </select>
        <p className="text-xs text-muted-foreground">
          How hard the model thinks. Higher = slower but more thorough. Takes
          effect on next agent restart.
        </p>
      </div>

      <div className="flex items-center justify-between rounded-md border border-border p-4">
        <div>
          <Label htmlFor="disable_adaptive_thinking" className="text-base">
            Disable adaptive thinking
          </Label>
          <p className="text-sm text-muted-foreground">
            Force Claude to always reason, even on simple questions. Useful
            at high/max effort.
          </p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2">
          <span className="sr-only">Disable adaptive thinking</span>
          <input
            id="disable_adaptive_thinking"
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={values.disable_adaptive_thinking}
            onChange={(e) =>
              setValues((v) => ({
                ...v,
                disable_adaptive_thinking: e.target.checked,
              }))
            }
          />
        </label>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setValues(baseValues)}
          disabled={!isDirty}
        >
          Reset
        </Button>
        <Button type="submit" disabled={!isDirty}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
